
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
  defineComponent,
  nextTick
} from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { throttle } from 'lodash-es'
import smoothscroll from 'smoothscroll-polyfill'
import SimpleLinkList from '@/components/block/SimpleLinkList.vue'
import MagazineFooter from '@/components/block/MagazineFooter.vue'
import MagazineCardSlider from '@/components/block/MagazineCardSlider.vue'
import MagazineModal from '@/components/block/MagazineModal.vue'
import SocialButtons from '@/components/block/SocialButtons.vue'
import Button from '@/components/elements/Button.vue'
import Keywords from '@/components/elements/Keywords.vue'
import { useSpecial } from '@/provider/special'
import { useRecommend } from '@/provider/recommend'
import { isPC } from '@/utils/commonMethods'

// 目次がある場合にコンテンツにスクロール
const scrollContents = (ev: any) => {
  const target = ev.target

  if (target.tagName.toLowerCase() === 'a') {
    if (target.closest('.rtocList')) {
      ev.preventDefault()

      const id = target.getAttribute('href')
      const el = document.querySelector(id)

      window.scrollTo({
        top: el.offsetTop - 20,
        behavior: 'smooth'
      })
    }
  }
}

export default defineComponent({
  name: 'MagazinesDetail',
  components: {
    SimpleLinkList,
    Button,
    SocialButtons,
    MagazineFooter,
    MagazineCardSlider,
    MagazineModal,
    Keywords
  },

  setup() {
    smoothscroll.polyfill()
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const { state, loadContent, loadContents } = useSpecial()
    // ページが変わったときはコンテンツロード
    watch(
      () => route.params.id,
      (next) => {
        if (route.name === 'magazinesDetail') {
          loadContent(Number(next))
        }
      },
      { immediate: true }
    )
    const content = computed(() => state.content)
    const childCategories = computed(() => state.content.childCategories)
    const contentCategory = computed(() => childCategories.value[0])
    const parentCategorySlug = computed(() => state.content.parentCategorySlug)
    const parentCategoryName = computed(() => state.content.parentCategoryName)
    const parentCategories = computed(() => {
      if (parentCategorySlug.value === 'series') return state.seriesCategories
      if (parentCategorySlug.value === 'special') return state.specialCategories
    })

    // レコメンド記事取得
    const { recommendState, loadRecommends } = useRecommend()
    loadRecommends()
    const categoryRecommends = computed(() => {
      return `${parentCategorySlug.value}Recommends` as
        | 'specialRecommends'
        | 'seriesRecommends'
    })
    const recommends = computed(
      () => recommendState[categoryRecommends.value]?.slice(0, 12) ?? []
    )

    // 連載コンテンツの場合は関連一覧記事リンク表示
    const seriesCategory = computed(() =>
      contentCategory?.value?.catSeries ? contentCategory.value : null
    )
    const seriesCategoryName = computed(() => seriesCategory.value?.name)
    // ページロードして、連載コンテンツカテゴリが変わったときは連載コンテンツをロード
    watch(
      () => seriesCategory.value?.id,
      (next, prev) => {
        if (next === prev) return
        loadContents({ perPage: 4, category: next })
      },
      { immediate: true }
    )
    const seriesContents = computed(() =>
      state.contents.map((content) => ({
        heading: content.title,
        link: content.path,
        current: state.content?.id === content.id
      }))
    )

    const isSp = ref(true)
    const onResize = throttle(() => {
      isSp.value = !isPC()
    }, 300)
    onMounted(() => {
      onResize()
      window.addEventListener('resize', onResize)
      document.addEventListener('click', scrollContents)
    })
    onUnmounted(() => {
      window.removeEventListener('resize', onResize)
      document.removeEventListener('click', scrollContents)
    })

    // タイトル、親カテゴリが変わった時にmeta, パンくず修正
    const title = computed((): string => state.content.title)
    const description = computed((): string => state.content.description)
    watch(
      () => [
        title.value,
        parentCategoryName.value,
        description.value,
        parentCategorySlug.value
      ],
      (next) => {
        nextTick(() => {
          document.title = `${next[0]} | ${next[1]} | 未来のドクターへ、期待以上の出会いを。【マイナビRESIDENT】`
          document
            ?.querySelector('meta[name="description"]')
            ?.setAttribute('content', next[2])
          const lastBread = document.querySelector('.breadCrumb_list li span')
          if (lastBread) {
            lastBread.innerHTML = next[0]
          }
          const categoryLinkBread = document.querySelector(
            '.breadCrumb_list li:nth-child(2) a'
          )
          if (categoryLinkBread) {
            categoryLinkBread.innerHTML = `${next[1]} 一覧`
            categoryLinkBread.setAttribute('href', `/magazines/${next[3]}/`)
          }
        })
      },
      { immediate: true }
    )

    // メンバー限定コンテンツ Modal表示
    const isMagazineModalShow = ref(false)
    const isMembershipOnly = computed(() => state.content.membershipOnly)

    // 一定量スクロールするとmodal表示
    const checkShowModal = () => {
      const showModal = throttle(() => {
        const wpContent = document.querySelector('.wp-content')
        if (!wpContent) return
        const wpContentHeight = wpContent.clientHeight
        const showModalTop =
          wpContentHeight && wpContentHeight > window.innerHeight * 2
            ? window.innerHeight * 2
            : window.innerHeight
        if (window.pageYOffset > showModalTop) {
          isMagazineModalShow.value = true
        }
      }, 300)
      window.addEventListener('scroll', showModal)
    }

    watch(
      () => [state.content.content],
      (next) => {
        // メンバー限定でない or ログイン済みはコンテンツ表示
        if (!isMembershipOnly.value || store.state.isLogin) return
        // コンテンツが表示されてから、スクロール量に応じてmodal表示
        if (next[0].length > 0) {
          nextTick(() => {
            setTimeout(() => {
              checkShowModal()
            }, 1000)
          })
        }
      },
      { immediate: true }
    )

    const moveToSeriesIndex = () => {
      router.push(
        `/magazines/${parentCategorySlug.value}/category/${seriesCategory.value?.id}`
      )
    }
    const moveToParentCategoryIndex = () => {
      router.push(`/magazines/${parentCategorySlug.value}`)
    }

    return {
      isSp,
      recommends,
      content,
      seriesCategory,
      seriesCategoryName,
      parentCategories,
      parentCategoryName,
      moveToSeriesIndex,
      moveToParentCategoryIndex,
      seriesContents,
      isMagazineModalShow
    }
  }
})
